import {useEffect, useRef, useState} from 'react';
import {useRetrieveInboundBridgeMetadata} from './use-analytics-api';
import {BridgeType, NodeFilterData} from '../inbound-bridging';

export const MAX_ALLOWED_DATE_RANGE = 14;

export function useInboundBridgeFilterData(
    bridgeType: BridgeType,
    tenant: {business: string; country: string},
    clientConfiguration: any,
    createErrorListener: (header: string) => (e: any) => void,
    executeRetrieveInboundBridgeData: (params: any) => void
) {
    const [nodeFilterDataMap, setNodeFilterDataMap] = useState<
        Record<
            string,
            {
                balanceDateEarliest: string;
                balanceDateLatest: string;
                horizonPairs: {
                    dateUnit: string;
                    fromHorizon: number;
                    toHorizon: number;
                }[];
                granularityValues: {
                    [key: string]: string[];
                };
            }
        >
    >({});
    const [selectedFilter, setSelectedFilter] = useState<NodeFilterData | undefined>();

    const nodeFilterDataInitialized = useRef(false);
    const isLoadingMetadata = useRef<boolean>(true);

    const {
        execute: executeRetrieveInboundBridgeMetadata,
        status: inboundBridgeMetadataStatus,
        value: getInboundBridgeMetadataResponse,
    } = useRetrieveInboundBridgeMetadata(clientConfiguration, createErrorListener('RetrieveInboundBridgeMetadata failed'), [
        tenant,
    ]);

    isLoadingMetadata.current = ['idle', 'pending'].includes(inboundBridgeMetadataStatus);

    if (!nodeFilterDataInitialized.current) {
        executeRetrieveInboundBridgeMetadata({
            forecastType: 'sop',
            ...tenant,
            bridgeType,
        });
        nodeFilterDataInitialized.current = true;
    }

    useEffect(() => {
        if (
            !isLoadingMetadata.current &&
            getInboundBridgeMetadataResponse?.nodes &&
            getInboundBridgeMetadataResponse.nodes.length > 0
        ) {
            const response = Object.fromEntries(
                getInboundBridgeMetadataResponse.nodes.map((n) => [
                    n.node,
                    {
                        balanceDateEarliest: n.balanceDateEarliest,
                        balanceDateLatest: n.balanceDateLatest,
                        horizonPairs: n.horizonPairs
                            .filter((hp) => hp.dateUnit === 'WEEK')
                            .sort((a, b) => a.fromHorizon - b.fromHorizon)
                            .map((hp) => ({
                                dateUnit: hp.dateUnit,
                                fromHorizon: hp.fromHorizon,
                                toHorizon: hp.toHorizon,
                            })),
                        granularityValues: n.granularityValues,
                    },
                ])
            );

            setNodeFilterDataMap(response);

            const defaultNode = getInboundBridgeMetadataResponse.nodes[0].node;
            const {balanceDateEarliest, balanceDateLatest, horizonPairs, granularityValues} = response[defaultNode];

            const latestDate = new Date(balanceDateLatest);
            const earliestDate = new Date(balanceDateEarliest);
            const startDate = new Date(latestDate.getTime() - MAX_ALLOWED_DATE_RANGE * 24 * 60 * 60 * 1000);
            const cappedStartDate = startDate < earliestDate ? earliestDate : startDate;

            setSelectedFilter({
                node: defaultNode,
                balanceDateEarliest,
                balanceDateLatest,
                balanceDateStart: cappedStartDate.toISOString().split('T')[0],
                balanceDateEnd: latestDate.toISOString().split('T')[0],
                fromHorizonInWeeks: horizonPairs[0].fromHorizon,
                toHorizonInWeeks: horizonPairs[0].toHorizon,
                channels: granularityValues['channels'] ?? [],
                sourceLocations: granularityValues['sourceLocations'] ?? [],
                ibTypes: granularityValues['ibTypes'] ?? [],
                tempZones: granularityValues['tempZones'] ?? [],
            });

            executeRetrieveInboundBridgeData({
                ...tenant,
                forecastType: 'sop',
                bridgeType,
                filter: JSON.stringify({
                    node: defaultNode,
                    balanceDateStart: cappedStartDate.toISOString().split('T')[0],
                    balanceDateEnd: latestDate.toISOString().split('T')[0],
                    fromHorizonInWeeks: horizonPairs[0].fromHorizon,
                    toHorizonInWeeks: horizonPairs[0].toHorizon,
                }),
            });
        }
    }, [isLoadingMetadata.current]);

    return {
        nodeFilterDataMap,
        selectedFilter,
        setSelectedFilter,
        isLoadingMetadata: isLoadingMetadata.current,
    };
}
